import { INTERNAL_NOTE_CHANNEL_ID } from "../../../components/MessageHistory/ChannelSelector";
import i18n from "../../../i18n/config";
import {
  outboundChannelRequiresClientPhone,
  hasClientPhoneRegistered,
  resolveOutboundClientPhone,
  shouldSkipClientPhoneForWhatsappGroupSend,
} from "../../../utils/clientContactUtils";
import {
  sendPersonalWhatsappChatMessage,
  getPersonalChatKeyFromSyntheticConversationId,
} from "../../../api/client";
import { deliveryStatusFromPersonalWhatsappStatus } from "../../../components/MessageHistory/messageDeliveryStatus";
import { uploadOne } from "../../../hooks/useS3Upload";
import { composerHasTextPayload } from "../../../components/MessageHistory/composerHtmlUtils";
import {
  emailAttachmentLimitError,
  humanizeEmailSendError,
} from "../../../components/MessageHistory/emailCompose";

const fetchBlobFromUrl = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error("Falha ao preparar arquivo de áudio para envio.");
  }
  return await response.blob();
};

export default function useMessageHandler({
  selectedConversation,
  userData,
  messages,
  setMessages,
  applyMessagesIfActive,
  sendMessageMutation,
  updateMutation,
  onUpdateConversation,
}) {
  const getThrownApiMessage = (error) => {
    if (error == null) return null;
    if (typeof error === "string" && error.trim()) return error.trim();
    if (typeof error.message === "string" && error.message.trim()) return error.message.trim();
    const d = error.response?.data;
    if (d && typeof d.message === "string" && d.message.trim()) return d.message.trim();
    return null;
  };

  const getSendErrorMessage = (error) => {
    const code = String(error?.code || "").toLowerCase();
    const status = Number(error?.status || error?.statusCode || 0);
    if (status === 402 || code.startsWith("billing.")) {
      if (code === "billing.limit_disabled") {
        return i18n.t("billingErrors.limitDisabled", { ns: "app" });
      }
      if (typeof error?.message === "string" && error.message.trim()) {
        return error.message;
      }
      return i18n.t("billingErrors.limitExceeded", { ns: "app" });
    }
    if (code === "chat.channel_send_forbidden") {
      return (
        (typeof error?.message === "string" && error.message.trim()) ||
        "Seu cargo não pode enviar mensagens por este canal."
      );
    }
    if (status === 401 || status === 403) {
      return "Erro de autenticação com o canal. Verifique as credenciais nas configurações.";
    }
    if (status === 404) {
      return "O canal ou conversa não foi encontrado. Atualize a página e tente novamente.";
    }
    if (status === 429) {
      return "Limite de envios atingido. Aguarde alguns minutos e tente reenviar.";
    }
    if (status >= 500) {
      return "Erro no servidor. Aguarde alguns instantes e tente reenviar a mensagem.";
    }
    if (error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT") {
      return "A conexão expirou. Verifique sua internet e tente reenviar.";
    }
    if (!navigator.onLine) {
      return "Sem conexão com a internet. Verifique sua rede e tente reenviar.";
    }
    return null;
  };

  const patchMessagesForConversation = (conversationId, updater) => {
    if (typeof applyMessagesIfActive === "function") {
      return applyMessagesIfActive(conversationId, updater);
    }
    setMessages(updater);
    return true;
  };

  const addOptimisticMessage = (
    tempId,
    conversationId,
    { message, file, audioURL, channelType, channel, isImageFile, localImageUrl, location, contact, email }
  ) => {
    const hasAudio = !!audioURL;
    const hasLocation =
      location &&
      typeof location === "object" &&
      location.latitude != null &&
      location.longitude != null;
    const hasContact =
      contact &&
      typeof contact === "object" &&
      String(contact.name || "").trim() &&
      String(contact.phone || "").trim();
    const isInternalNoteDraft = String(channel) === String(INTERNAL_NOTE_CHANNEL_ID);
    const tsNow = Date.now();
    const base = {
      id: tempId,
      conversationId: String(conversationId),
      text: message || (file ? file.name || "Arquivo enviado" : ""),
      senderName: (userData?.nome || userData?.name || "").trim() || undefined,
      senderId: userData?.id,
      senderType: "agent",
      ts: tsNow,
      timestamp: new Date().toISOString(),
      file: file ? file.name : null,
      audio: hasAudio ? audioURL : null,
      ...(email ? { email, channel: channelType || "Email" } : {}),
      location: hasLocation
        ? {
            latitude: location.latitude,
            longitude: location.longitude,
            lat: location.latitude,
            lng: location.longitude,
            ...(location.name ? { name: location.name } : {}),
            ...(location.address ? { address: location.address } : {}),
          }
        : null,
      contact: hasContact
        ? {
            name: String(contact.name).trim(),
            phone: String(contact.phone).trim(),
            email: contact.email != null ? String(contact.email).trim() : null,
          }
        : null,
      // Para anexos enviados via anotação interna, a UI deve renderizar como "note"
      // (mantendo o layout de anotação) mesmo enquanto ainda está "sending".
      type: hasContact
        ? "contact"
        : hasLocation
        ? "location"
        : hasAudio
        ? "audio"
        : isInternalNoteDraft
        ? "note"
        : channelType || "note",
      status: "sending",
      // 1º tick local; após aceite da API vira id real + deliveryStatus do servidor.
      ...(isInternalNoteDraft
        ? {}
        : {
            deliveryStatus: { status: 0, label: "Enviando", errorReason: null },
          }),
      lastUpdate: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      // `channel` permanece o rótulo de exibição ("SMS"); o id numérico vai em channelId
      // para o "Tentar reenviar" não cair no canal da conversa (ex.: WhatsApp).
      channel: channelType,
      channelType: channelType || null,
      channelId: channel != null && String(channel).trim() !== "" ? channel : null,
    };
    const enriched =
      isImageFile && localImageUrl
        ? isInternalNoteDraft
          ? { ...base, image: localImageUrl }
          : { ...base, type: "image", image: localImageUrl }
        : { ...base, text: base.text || (email || hasLocation || hasContact ? "" : "Arquivo enviado") };
    patchMessagesForConversation(conversationId, (prev) => {
      const updated = new Map(prev);
      updated.set(tempId, enriched);
      return updated;
    });
  };

  const handleSendMessage = async (payload, retryId = null) => {
    if (!selectedConversation?.id) return;

    const conversationId = String(selectedConversation.id);
    const isPersonalConv =
      selectedConversation?.kind === "personal" ||
      String(selectedConversation?.id || "").startsWith("wa-personal:");
    const personalChatKey =
      selectedConversation?.personalChatKey ||
      getPersonalChatKeyFromSyntheticConversationId(selectedConversation?.id);

    const {
      message,
      file,
      files,
      audioURL,
      channel,
      channelType,
      transferTo,
      templateId,
      metaMessagingTag,
      emailTo,
      emailSubject,
      emailCc,
      location: locationPayload,
      contact: contactPayload,
      buttonMessage,
      mentions,
      mentionNames,
      mentionAll,
      type: requestedType,
      pollOptions,
      selectableCount,
      transport,
      quotedMessageId,
      quotedParticipant,
    } = payload;
    const filesList = Array.isArray(files) && files.length > 0 ? files : file ? [file] : [];

    const hasValidLocation =
      locationPayload &&
      typeof locationPayload === "object" &&
      locationPayload.latitude != null &&
      locationPayload.longitude != null &&
      Number.isFinite(Number(locationPayload.latitude)) &&
      Number.isFinite(Number(locationPayload.longitude)) &&
      !(Number(locationPayload.latitude) === 0 && Number(locationPayload.longitude) === 0);

    const hasValidContact =
      contactPayload &&
      typeof contactPayload === "object" &&
      String(contactPayload.name || "").trim() &&
      String(contactPayload.phone || "").trim();

    const hasSendPayload =
      (message != null && String(message).trim() !== "") ||
      filesList.length > 0 ||
      !!audioURL ||
      !!hasValidLocation ||
      !!hasValidContact ||
      requestedType === "poll";
    const isInternalNote = String(channel) === String(INTERNAL_NOTE_CHANNEL_ID);
    if (
      hasSendPayload &&
      !isInternalNote &&
      !isPersonalConv &&
      outboundChannelRequiresClientPhone(channel, channelType) &&
      !hasClientPhoneRegistered(selectedConversation) &&
      !shouldSkipClientPhoneForWhatsappGroupSend({
        conversation: selectedConversation,
        messages,
        channelType,
      })
    ) {
      return;
    }

    if (transferTo && updateMutation?.mutateAsync && !isPersonalConv) {
      try {
        // Importante: ao transferir para agente, NÃO enviar `id_usuario` (mesmo como `null`),
        // para que o backend não interprete como "remoção explícita de atribuição do usuário".
        // O mesmo vale para transferir para usuário (não enviar `id_agente`).
        await updateMutation.mutateAsync({
          id: conversationId,
          ...(transferTo.type === "user" ? { id_usuario: transferTo.id } : { id_agente: transferTo.id }),
        });
        const updates =
          transferTo.type === "user"
            ? {
                userId: transferTo.id,
                id_agente: null,
                agentId: null,
                agentName: null,
                userName: transferTo.label ?? null,
                assignedTo: transferTo.label ?? null,
              }
            : {
                userId: null,
                id_agente: transferTo.id,
                agentId: transferTo.id,
                agentName: transferTo.label ?? null,
                userName: null,
                assignedTo: null,
              };
        onUpdateConversation?.(updates);
      } catch (err) {
        console.error("Erro ao transferir solicitação:", err);
      }
    }

    const baseTime = Date.now();
    const sendItems = [];
    const isEmailChannel = String(channelType || "").toLowerCase() === "email";
    if (isEmailChannel) {
      const limitError = emailAttachmentLimitError(filesList);
      if (limitError) return { ok: false, error: limitError };
      const emailText = composerHasTextPayload(message) ? String(message).trim() : "";
      if (!emailText && filesList.length === 0) {
        return { ok: false, error: "Escreva a mensagem ou anexe um arquivo para enviar." };
      }
      sendItems.push({ type: "email", message: emailText, files: filesList });
    } else {
      if (requestedType === "poll" && Array.isArray(pollOptions) && pollOptions.filter(Boolean).length >= 2) {
        sendItems.push({
          type: "poll",
          message: message != null ? String(message).trim() : "",
          pollOptions: pollOptions.map((o) => String(o || "").trim()).filter(Boolean),
          selectableCount,
        });
      } else if (message != null && String(message).trim()) {
        sendItems.push({ type: "text", message: message.trim() });
      }
      filesList.forEach((f) => sendItems.push({ type: "file", file: f }));
    }
    if (audioURL) {
      sendItems.push({ type: "audio", audioURL });
    }
    if (hasValidLocation) {
      sendItems.push({
        type: "location",
        location: {
          latitude: Number(locationPayload.latitude),
          longitude: Number(locationPayload.longitude),
          ...(locationPayload.name ? { name: String(locationPayload.name) } : {}),
          ...(locationPayload.address ? { address: String(locationPayload.address) } : {}),
        },
      });
    }
    if (hasValidContact) {
      sendItems.push({
        type: "contact",
        contact: {
          name: String(contactPayload.name).trim(),
          phone: String(contactPayload.phone).trim(),
          ...(contactPayload.email != null && String(contactPayload.email).trim()
            ? { email: String(contactPayload.email).trim() }
            : {}),
          ...(contactPayload.company != null && String(contactPayload.company).trim()
            ? { company: String(contactPayload.company).trim() }
            : {}),
        },
      });
    }

    if (sendItems.length === 0) return;

    const applyTemplateAndTransfer = (index) => ({
      ...(metaMessagingTag != null && { metaMessagingTag }),
      ...(index === 0
        ? {
            ...(transferTo && { transferTo }),
            ...(templateId != null && { templateId }),
            ...(emailTo != null && { emailTo }),
            ...(emailSubject != null && { emailSubject }),
            ...(emailCc != null && { emailCc }),
          }
        : {}),
    });

    for (let i = 0; i < sendItems.length; i++) {
      const item = sendItems[i];
      const tempId = `temp-${baseTime}-${i}`;

      if (retryId && i === 0) {
        patchMessagesForConversation(conversationId, (prev) => {
          const updated = new Map(prev);
          updated.delete(retryId);
          return updated;
        });
      }

      const isImageFile =
        item.type === "file" &&
        item.file &&
        typeof item.file.type === "string" &&
        item.file.type.startsWith("image/");
      const localImageUrl = isImageFile && item.file ? URL.createObjectURL(item.file) : null;
      const emailAttachments =
        item.type === "email"
          ? (item.files || []).map((emailFile) => ({
              name: emailFile.name,
              type: emailFile.type,
              size: emailFile.size,
              url: URL.createObjectURL(emailFile),
            }))
          : [];

      addOptimisticMessage(tempId, conversationId, {
        message: item.type === "text" || item.type === "email" || item.type === "poll" ? item.message : "",
        file: item.type === "file" ? item.file : null,
        audioURL: item.type === "audio" ? item.audioURL : null,
        channelType,
        channel,
        isImageFile,
        localImageUrl,
        location: item.type === "location" ? item.location : null,
        contact: item.type === "contact" ? item.contact : null,
        ...(item.type === "email"
          ? {
              email: {
                direction: "outbound",
                to: emailTo || selectedConversation?.email || null,
                cc: Array.isArray(emailCc) ? emailCc : [],
                subject: emailSubject || null,
                attachments: emailAttachments,
              },
            }
          : {}),
      });

      try {
        if (isPersonalConv && !isInternalNote) {
          if (!personalChatKey) {
            console.warn("WhatsApp pessoal: chatKey ausente.");
            continue;
          }
          if (item.type === "location") {
            patchMessagesForConversation(conversationId, (prev) => {
              const updated = new Map(prev);
              const errored = updated.get(tempId);
              if (!errored) return prev;
              updated.set(tempId, {
                ...errored,
                status: "error",
                errorMessage: i18n.t("pane.personalMediaNotSupported", { ns: "chat" }),
              });
              return updated;
            });
            continue;
          }
          if (item.type === "contact") {
            patchMessagesForConversation(conversationId, (prev) => {
              const updated = new Map(prev);
              const errored = updated.get(tempId);
              if (!errored) return prev;
              updated.set(tempId, {
                ...errored,
                status: "error",
                errorMessage: i18n.t("shareContact.notSupportedPersonal", { ns: "chat" }),
              });
              return updated;
            });
            continue;
          }

          let sendPayload;
          if (item.type === "text") {
            sendPayload = { text: item.message };
          } else if (item.type === "audio") {
            const audioBlob = await fetchBlobFromUrl(item.audioURL);
            const mimeType =
              audioBlob.type && audioBlob.type !== "audio/mp3" ? audioBlob.type : "audio/mpeg";
            const fileName = `audio-${Date.now()}.mp3`;
            const audioFile = new File([audioBlob], fileName, { type: mimeType });
            const uploaded = await uploadOne(audioFile, { prefix: "chat" });
            sendPayload = { type: "audio", audio: uploaded.url };
          } else if (item.type === "file" && item.file) {
            const uploaded = await uploadOne(item.file, { prefix: "chat" });
            const isImg = typeof item.file.type === "string" && item.file.type.startsWith("image/");
            const isVid = typeof item.file.type === "string" && item.file.type.startsWith("video/");
            sendPayload = {
              type: isImg ? "image" : isVid ? "video" : "file",
              text: "",
              file: {
                url: uploaded.url,
                name: item.file.name,
                type: item.file.type,
                size: item.file.size,
              },
            };
          } else {
            continue;
          }

          try {
            const result = await sendPersonalWhatsappChatMessage(personalChatKey, sendPayload);
            const saved = result?.data?.message ?? result?.message ?? result?.data;
            patchMessagesForConversation(conversationId, (prev) => {
              const updated = new Map(prev);
              const original = updated.get(tempId);
              if (!original) return prev;
              const newId = saved?.id ?? saved?.insertedId ?? null;
              const createdAt = saved?.ts ? new Date(saved.ts).toISOString() : null;
              const personalDs = deliveryStatusFromPersonalWhatsappStatus(saved?.statusWhatsapp);
              const personalSender = (userData?.nome || userData?.name || "").trim() || original.senderName;
              const waId = saved?.waMessageId != null ? String(saved.waMessageId).trim() : null;
              const uploadedUrl =
                sendPayload?.file?.url != null && String(sendPayload.file.url).trim()
                  ? String(sendPayload.file.url).trim()
                  : sendPayload?.audio
                    ? String(sendPayload.audio).trim()
                    : "";
              const personalMedia = {};
              if (uploadedUrl) {
                if (sendPayload.type === "image") {
                  personalMedia.image = uploadedUrl;
                  personalMedia.type = "image";
                } else if (sendPayload.type === "video") {
                  personalMedia.video = uploadedUrl;
                  personalMedia.type = "video";
                } else if (sendPayload.type === "audio") {
                  personalMedia.audio = uploadedUrl;
                  personalMedia.type = "audio";
                } else if (sendPayload.type === "file") {
                  personalMedia.document = {
                    url: uploadedUrl,
                    name: sendPayload.file?.name || original.text || "Documento",
                  };
                  personalMedia.type = "document";
                }
              }
              if (localImageUrl) URL.revokeObjectURL(localImageUrl);
              if (!newId) {
                updated.set(tempId, {
                  ...original,
                  conversationId,
                  status: "sent",
                  createdAt: createdAt || original.createdAt,
                  deliveryStatus: personalDs,
                  ...(waId ? { waMessageId: waId } : {}),
                  ...(personalSender ? { senderName: personalSender } : {}),
                  ...personalMedia,
                });
                return updated;
              }
              updated.set(newId, {
                ...original,
                id: newId,
                conversationId,
                status: "sent",
                createdAt: createdAt || original.createdAt,
                deliveryStatus: personalDs,
                ...(waId ? { waMessageId: waId } : {}),
                ...(personalSender ? { senderName: personalSender } : {}),
                ...personalMedia,
              });
              updated.delete(tempId);
              return updated;
            });
          } catch (err) {
            const msg = getThrownApiMessage(err) || getSendErrorMessage(err) || "Falha ao enviar.";
            patchMessagesForConversation(conversationId, (prev) => {
              const updated = new Map(prev);
              const errored = updated.get(tempId);
              if (!errored) return prev;
              updated.set(tempId, {
                ...errored,
                status: "error",
                errorMessage: msg,
                deliveryStatus: {
                  status: -1,
                  label: "Erro",
                  errorReason: msg || "send_failed",
                },
              });
              return updated;
            });
          }
          continue;
        }

        const mutationPayload = {
          conversationId,
          phone: resolveOutboundClientPhone(selectedConversation, channelType),
          email: selectedConversation?.email || "",
          message: item.type === "text" || item.type === "email" || item.type === "poll" ? item.message : "",
          userName: (userData?.nome || userData?.name || "").trim() || undefined,
          file: item.type === "file" ? item.file : undefined,
          files: item.type === "email" ? item.files : undefined,
          audioURL: item.type === "audio" ? item.audioURL : undefined,
          ...(item.type === "location"
            ? {
                location: item.location,
                ...(item.location?.name ? { locationName: item.location.name } : {}),
                ...(item.location?.address ? { locationAddress: item.location.address } : {}),
              }
            : {}),
          ...(item.type === "contact" ? { contact: item.contact } : {}),
          ...(i === 0 && buttonMessage ? { buttonMessage } : {}),
          ...(i === 0 && Array.isArray(mentions) && mentions.length ? { mentions } : {}),
          ...(i === 0 && mentionNames && typeof mentionNames === "object" && !Array.isArray(mentionNames)
            ? { mentionNames }
            : {}),
          ...(i === 0 && mentionAll === true ? { mentionAll: true } : {}),
          ...(item.type === "poll"
            ? { type: "poll", pollOptions: item.pollOptions, selectableCount: item.selectableCount }
            : {}),
          ...(quotedMessageId ? { quotedMessageId } : {}),
          ...(quotedParticipant ? { quotedParticipant } : {}),
          channel,
          ...(transport === "webchat" || transport === "whatsapp" ? { transport } : {}),
          projectId: selectedConversation.projectId,
          userId: userData?.id,
          ...applyTemplateAndTransfer(i),
        };
        const result = await sendMessageMutation.mutateAsync(mutationPayload);
        const resultData = result?.data ?? result ?? {};
        const sentFileMeta = result?.__sentFile;
        const sentFiles = Array.isArray(result?.__sentFiles) ? result.__sentFiles : [];

        patchMessagesForConversation(conversationId, (prev) => {
          const updated = new Map(prev);
          const original = updated.get(tempId);
          if (!original) return prev;

          // Troca blob: local pelo URL permanente do S3 antes de revogar o preview.
          // Sem isso a bolha fica com "Mídia indisponível ou expirada" até o reload.
          const permanentUrl =
            sentFileMeta?.url != null && String(sentFileMeta.url).trim()
              ? String(sentFileMeta.url).trim()
              : "";
          const mime = String(sentFileMeta?.type || item.file?.type || "").toLowerCase();
          const uploadedIsImage = Boolean(isImageFile || mime.startsWith("image/"));
          const uploadedIsVideo = mime.startsWith("video/");
          const mediaFromUpload = {};
          if (permanentUrl) {
            if (uploadedIsImage) {
              mediaFromUpload.image = permanentUrl;
              if (original.type !== "note") mediaFromUpload.type = "image";
            } else if (uploadedIsVideo) {
              mediaFromUpload.video = permanentUrl;
              if (original.type !== "note") mediaFromUpload.type = "video";
            } else {
              mediaFromUpload.document = {
                url: permanentUrl,
                name: sentFileMeta?.name || original.text || "Documento",
              };
              if (original.type !== "note") mediaFromUpload.type = "document";
            }
          }

          if (localImageUrl) URL.revokeObjectURL(localImageUrl);
          if (item.type === "email") {
            emailAttachments.forEach((file) => {
              if (file.url && sentFiles.length) URL.revokeObjectURL(file.url);
            });
          }
          const emailFromUpload =
            item.type === "email"
              ? {
                  email: {
                    direction: "outbound",
                    to: emailTo || selectedConversation?.email || null,
                    cc: Array.isArray(emailCc) ? emailCc : [],
                    subject: emailSubject || null,
                    attachments: (sentFiles.length ? sentFiles : emailAttachments).map((file) => ({
                      name: file.name,
                      url: file.url,
                      type: file.type,
                      size: file.size,
                    })),
                  },
                }
              : {};

          const newId = resultData.id ?? resultData.insertedId ?? resultData.messageId ?? resultData?.data?.id;
          const createdAt = resultData.createdAt ?? null;
          const updatedAt = resultData.updatedAt ?? null;
          // Persist-first WhatsApp: no aceite já tratamos como Enviado; entrega/lida/erro
          // chegam depois via webhook/socket. Compat: se API antiga ainda mandar 0, promove.
          let deliveryStatus =
            resultData.deliveryStatus ??
            original.deliveryStatus ??
            null;
          const asyncAccepted = !!(resultData.asyncAccepted || result?.asyncAccepted);
          const isPendingDelivery =
            deliveryStatus &&
            (deliveryStatus.status === 0 ||
              deliveryStatus.status === "sending" ||
              deliveryStatus.status === "queued" ||
              String(deliveryStatus.label || "").toLowerCase() === "enviando");
          // Persist-first WhatsApp (asyncAccepted) e SMS síncrono: no aceite da API
          // já mostramos Enviado; entrega/falha finas chegam depois via webhook.
          if (isPendingDelivery && (asyncAccepted || resultData.deliveryStatus == null)) {
            deliveryStatus = { status: 1, label: "Enviado", errorReason: null };
          }
          if (!newId) {
            // fallback: mantém tempId, mas marca como enviado para não ficar preso em "Enviando..."
            updated.set(tempId, {
              ...original,
              conversationId,
              status: "sent",
              createdAt: createdAt || original.createdAt,
              updatedAt: updatedAt || original.updatedAt,
              deliveryStatus: deliveryStatus || original.deliveryStatus,
              ...mediaFromUpload,
              ...emailFromUpload,
            });
            return updated;
          }
          updated.set(newId, {
            ...original,
            id: newId,
            conversationId,
            status: "sent",
            createdAt: createdAt || original.createdAt,
            updatedAt: updatedAt || original.updatedAt,
            lastUpdate: updatedAt || createdAt || original.lastUpdate,
            deliveryStatus,
            ...mediaFromUpload,
            ...emailFromUpload,
          });
          updated.delete(tempId);
          return updated;
        });
      } catch (error) {
        console.error("Erro ao enviar mensagem:", error);
        if (item.type === "email") {
          emailAttachments.forEach((file) => {
            if (file.url) URL.revokeObjectURL(file.url);
          });
          const emailError = humanizeEmailSendError(
            getThrownApiMessage(error) || getSendErrorMessage(error),
            { hadAttachments: (item.files || []).length > 0 }
          );
          patchMessagesForConversation(conversationId, (prev) => {
            const updated = new Map(prev);
            updated.delete(tempId);
            return updated;
          });
          return { ok: false, error: emailError };
        }
        // Não revoga localImageUrl para manter o preview visível na mensagem com status "error"
        const friendlyError = getSendErrorMessage(error);
        const apiText = getThrownApiMessage(error);
        patchMessagesForConversation(conversationId, (prev) => {
          const updated = new Map(prev);
          const errored = updated.get(tempId);
          if (!errored) return prev;
          updated.set(tempId, {
            ...errored,
            status: "error",
            errorMessage: friendlyError || apiText || errored.errorMessage || null,
            deliveryStatus: {
              status: -1,
              label: "Erro",
              errorReason: friendlyError || apiText || "send_failed",
            },
          });
          return updated;
        });
      }
    }
    return { ok: true };
  };

  return { handleSendMessage };
}
